import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { JobConflict } from '@/lib/conflicts';

/**
 * The warning on a job that clashes (M1-12).
 *
 * <b>A warning, and nothing more.</b> The AC is explicit that dispatchers
 * double-book deliberately, so this never disables a card, blocks a drop, or
 * asks anyone to confirm anything. It says what is true and gets out of the
 * way.
 *
 * Words, not just a colour or an icon. `⚠` is decorative and
 * `aria-hidden`; the text beside it is what carries the meaning, for a
 * colourblind dispatcher and a screen reader alike (web/CONVENTIONS.md:
 * "status is never colour-only"). It uses the already-verified
 * `--color-destructive` on `--color-background` pairing, so it adds no new
 * palette token to check.
 */
export function ConflictBadge({
  conflicts,
  compact = false,
}: {
  conflicts: JobConflict[];
  /** The week view's chips have room for the icon and a short word only. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (conflicts.length === 0) {
    return null;
  }

  return (
    <span
      className={cn(
        'flex flex-wrap items-center gap-1 text-[var(--color-destructive)]',
        compact ? 'text-[0.625rem]' : 'text-[0.625rem] font-medium',
      )}
      data-testid="conflict-badge"
    >
      <span aria-hidden="true">⚠</span>
      {conflicts.map((conflict) => (
        <span key={conflict.kind}>
          {compact
            ? t(`dispatch.conflict.short.${conflict.kind}`)
            : conflict.kind === 'overlap'
              ? t('dispatch.conflict.overlap', { count: conflict.withJobIds.length })
              : t(`dispatch.conflict.${conflict.kind}`)}
        </span>
      ))}
    </span>
  );
}

/**
 * The same thing as a sentence, for a card's accessible name.
 *
 * A badge rendered inside a `<button>` is read as part of that button's
 * content only if the button has no `aria-label` — and these cards do have
 * one, so the warning has to be folded into it explicitly or it is invisible
 * to a screen reader.
 */
export function describeConflicts(
  conflicts: JobConflict[] | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!conflicts || conflicts.length === 0) {
    return '';
  }
  return conflicts
    .map((conflict) =>
      conflict.kind === 'overlap'
        ? t('dispatch.conflict.overlap', { count: conflict.withJobIds.length })
        : t(`dispatch.conflict.${conflict.kind}`),
    )
    .join('. ');
}
